import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  ReferenceLine
} from 'recharts';
import { formatCurrency } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import io from 'socket.io-client';
import './embed.css';

const EmbedStockDetail = () => {
  const { ticker } = useParams();
  const [searchParams] = useSearchParams();

  // URL params for customization
  const bg = searchParams.get('bg') || '#0a0a0a';
  const accentColor = searchParams.get('accent') || '#00ff88';
  const chartHeightParam = searchParams.get('chartHeight');
  const chartHeight = chartHeightParam ? parseInt(chartHeightParam) : 200;
  const useFlexChart = !chartHeightParam; // No explicit height = let flex handle sizing
  const refreshInterval = parseInt(searchParams.get('refresh')) || 5000;
  const layout = searchParams.get('layout') || 'vertical'; // vertical, horizontal, compact

  // Section visibility — individual params OR comma-separated `sections` shorthand
  // e.g. ?sections=chart,price  or  ?ceo=false&stats=false
  const sectionsParam = searchParams.get('sections');
  const sectionSet = sectionsParam ? new Set(sectionsParam.split(',').map(s => s.trim().toLowerCase())) : null;

  const showCompany = sectionSet ? sectionSet.has('company') || sectionSet.has('ticker') : searchParams.get('company') !== 'false';
  const showDescription = sectionSet ? sectionSet.has('description') || sectionSet.has('desc') : searchParams.get('description') !== 'false';
  const showCeo = sectionSet ? sectionSet.has('ceo') : searchParams.get('ceo') !== 'false';
  const showChart = sectionSet ? sectionSet.has('chart') : searchParams.get('chart') !== 'false';
  const showPrice = sectionSet ? sectionSet.has('price') : searchParams.get('price') !== 'false';
  const showStats = sectionSet ? sectionSet.has('stats') : searchParams.get('stats') !== 'false';

  const [stockData, setStockData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      // Fetch stock info with detailed data
      const stockResponse = await fetch(`${API_BASE_URL}/api/stocks/ticker/${ticker}`);
      if (!stockResponse.ok) {
        // Don't throw — just keep showing placeholder and retry on next interval
        setLoading(false);
        return;
      }
      const stock = await stockResponse.json();
      setStockData(stock);

      // Fetch chart data - get recent history
      const historyResponse = await fetch(
        `${API_BASE_URL}/api/stocks/${stock.id}/history?interval=1m&limit=60`
      );

      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        const formattedData = historyData.data.map((candle, index) => ({
          index,
          time: new Date(candle.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          }),
          open: candle.open,
          high: candle.high,
          low: candle.low,
          close: candle.close,
          volume: candle.volume
        }));
        setChartData(formattedData);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching stock detail:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Set up socket for real-time updates
    const socket = io(API_BASE_URL, { auth: { public: true } });

    socket.on('stock-updates', (updates) => {
      const stocksArray = Array.isArray(updates) ? updates : updates?.stocks;
      if (!stocksArray) return;
      const update = stocksArray.find(u =>
        u.ticker?.toUpperCase() === ticker?.toUpperCase()
      );
      if (update) {
        const livePrice = parseFloat(update.currentPrice || update.price);
        if (!livePrice || isNaN(livePrice)) return;
        setStockData(prev => {
          if (!prev) return prev;
          const basePrice = prev.basePrice || prev.currentPrice;
          const change24h = livePrice - basePrice;
          const changePercent24h = basePrice > 0 ? ((livePrice - basePrice) / basePrice) * 100 : 0;
          return {
            ...prev,
            currentPrice: livePrice,
            change24h: parseFloat(change24h.toFixed(4)),
            changePercent24h: parseFloat(changePercent24h.toFixed(2)),
          };
        });
      }
    });

    const interval = setInterval(fetchData, refreshInterval);

    return () => {
      socket.disconnect();
      clearInterval(interval);
    };
  }, [ticker, refreshInterval]);

  if (loading) {
    return (
      <div className="embed-stock-detail" style={{ background: bg }}>
        <div className="embed-loading">Loading {ticker}...</div>
      </div>
    );
  }

  // If error or no data yet, show a graceful placeholder frame
  if (error || !stockData) {
    return (
      <div
        className={`embed-stock-detail layout-${layout}`}
        style={{
          background: bg === 'transparent' ? 'transparent' : bg,
          '--accent-color': accentColor,
          '--change-color': '#666'
        }}
      >
        <div className="stock-company-section">
          <div className="stock-ticker-large">{ticker?.toUpperCase() || '---'}</div>
          <div className="stock-company-info">
            <span className="stock-name" style={{ color: '#555' }}>Loading...</span>
          </div>
        </div>
        <div className="stock-price-section">
          <div className="stock-current-price" style={{ color: '#555' }}>--</div>
          <div className="stock-change" style={{ color: '#555' }}>
            <span className="change-percent">--.--% </span>
          </div>
        </div>
      </div>
    );
  }

  const isPositive = stockData.changePercent24h >= 0;
  const changeColor = isPositive ? '#00ff88' : '#ff4444';

  // Calculate chart domain
  const prices = chartData.flatMap(d => [d.high, d.low]).filter(p => p != null);
  const minPrice = prices.length > 0 ? Math.min(...prices) * 0.998 : stockData.currentPrice * 0.95;
  const maxPrice = prices.length > 0 ? Math.max(...prices) * 1.002 : stockData.currentPrice * 1.05;

  // Candlestick rendering
  const renderCandlestick = (data) => {
    return chartData.map((entry, index) => {
      const isUp = entry.close >= entry.open;
      const color = isUp ? '#00ff88' : '#ff4444';
      const bodyTop = Math.max(entry.open, entry.close);
      const bodyBottom = Math.min(entry.open, entry.close);

      return {
        ...entry,
        candleColor: color,
        wickHigh: entry.high,
        wickLow: entry.low,
        bodyTop,
        bodyBottom,
        bodyHeight: Math.max(bodyTop - bodyBottom, 0.01)
      };
    });
  };

  const candleData = renderCandlestick(chartData);

  return (
    <div
      className={`embed-stock-detail layout-${layout}`}
      style={{
        background: bg === 'transparent' ? 'transparent' : bg,
        '--accent-color': accentColor,
        '--change-color': changeColor
      }}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>

      {/* Company Section - ticker big, name+description small inline */}
      {showCompany && (
        <div className="stock-company-section">
          <div className="stock-ticker-large">{stockData.ticker}</div>
          <div className="stock-company-info">
            <span className="stock-name">{stockData.name}</span>
            {showDescription && stockData.description && (
              <span className="stock-description">{stockData.description}</span>
            )}
          </div>
        </div>
      )}

      {/* CEO Section */}
      {showCeo && (
        <div className="stock-ceo-section">
          {stockData.ceo_picture_url && (
            <div className="ceo-picture-container">
              <img
                src={stockData.ceo_picture_url}
                alt={`${stockData.ceo_name} - CEO`}
                className="ceo-picture"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          <div className="ceo-info">
            <div className="ceo-title">Chief Executive Officer</div>
            <div className="ceo-name">{stockData.ceo_name || 'TBD'}</div>
            <div className="ceo-description">{stockData.ceo_description || 'CEO information coming soon.'}</div>
          </div>
        </div>
      )}

      {/* Candlestick Chart */}
      {showChart && chartData.length > 0 && (
        <div className="stock-chart-container" style={useFlexChart ? {} : { height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={candleData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <XAxis
                dataKey="time"
                tick={{ fontSize: 9, fill: '#666' }}
                axisLine={{ stroke: '#333' }}
                tickLine={{ stroke: '#333' }}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[minPrice, maxPrice]}
                tick={{ fontSize: 9, fill: '#666' }}
                axisLine={{ stroke: '#333' }}
                tickLine={{ stroke: '#333' }}
                tickFormatter={v => formatCurrency(v)}
                orientation="right"
                width={55}
              />

              {/* Reference line at current price */}
              <ReferenceLine
                y={stockData.currentPrice}
                stroke={changeColor}
                strokeDasharray="3 3"
                strokeOpacity={0.5}
              />

              {/* Wicks */}
              {candleData.map((entry, index) => (
                <ReferenceLine
                  key={`wick-${index}`}
                  segment={[
                    { x: entry.time, y: entry.wickLow },
                    { x: entry.time, y: entry.wickHigh }
                  ]}
                  stroke={entry.candleColor}
                  strokeWidth={1}
                />
              ))}

              {/* Candle bodies as bars */}
              <Bar
                dataKey="bodyHeight"
                barSize={6}
                shape={(props) => {
                  const { x, y, width, height, payload } = props;
                  const yPos = maxPrice - payload.bodyTop;
                  const scaledY = (yPos / (maxPrice - minPrice)) * (chartHeight - 20) + 10;
                  const scaledHeight = (payload.bodyHeight / (maxPrice - minPrice)) * (chartHeight - 20);

                  return (
                    <g>
                      {/* Wick */}
                      <line
                        x1={x + width / 2}
                        y1={(1 - (payload.wickHigh - minPrice) / (maxPrice - minPrice)) * (chartHeight - 20) + 10}
                        x2={x + width / 2}
                        y2={(1 - (payload.wickLow - minPrice) / (maxPrice - minPrice)) * (chartHeight - 20) + 10}
                        stroke={payload.candleColor}
                        strokeWidth={1}
                      />
                      {/* Body */}
                      <rect
                        x={x}
                        y={(1 - (payload.bodyTop - minPrice) / (maxPrice - minPrice)) * (chartHeight - 20) + 10}
                        width={width}
                        height={Math.max(scaledHeight, 2)}
                        fill={payload.candleColor}
                        stroke={payload.candleColor}
                      />
                    </g>
                  );
                }}
              >
                {candleData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.candleColor} />
                ))}
              </Bar>

              {/* Price line overlay */}
              <Line
                type="monotone"
                dataKey="close"
                stroke={changeColor}
                strokeWidth={1.5}
                dot={false}
                strokeOpacity={0.6}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Price Section - at bottom */}
      {showPrice && (
        <div className="stock-price-section">
          <div className="stock-current-price">{formatCurrency(stockData.currentPrice)}</div>
          <div className={`stock-change ${isPositive ? 'positive' : 'negative'}`}>
            <span className="change-arrow">{isPositive ? '▲' : '▼'}</span>
            <span className="change-percent">
              {isPositive ? '+' : ''}{stockData.changePercent24h?.toFixed(2)}%
            </span>
            <span className="change-amount">
              ({isPositive ? '+' : ''}{formatCurrency(stockData.change24h)})
            </span>
          </div>
        </div>
      )}

      {/* Stats Row */}
      {showStats && stockData.stats && (
        <div className="stock-stats-row">
          <div className="stat-item">
            <span className="stat-label">Open</span>
            <span className="stat-value">{formatCurrency(stockData.basePrice)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">High</span>
            <span className="stat-value positive">{formatCurrency(stockData.stats.dayHigh)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Low</span>
            <span className="stat-value negative">{formatCurrency(stockData.stats.dayLow)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Volume</span>
            <span className="stat-value">{stockData.stats.totalVolume?.toLocaleString() || '0'}</span>
          </div>
        </div>
      )}

    </div>
  );
};

export default EmbedStockDetail;
